<template>
  <div class="sales-rep-sections-grid">
    <div class="row">
      <!-- Today's Callbacks -->
      <div class="col-md-12 d-flex">
        <div class="card flex-fill sales-rep-widget-card">
          <div class="section-loader-overlay" v-if="isLoading">
            <span class="loader"></span>
          </div>
          <div class="card-body p-0">
            <div class="statistic-header p-3 border-bottom mb-0">
              <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 w-100">
                <div>
                  <h4 class="mb-1">
                    <router-link to="/crm/meter-opportunities-list">Today's Quoted, Priority and Decision Maker Callbacks</router-link>
                  </h4>
                  <!-- <p v-if="pipelineFilterLabel" class="mb-0 small sales-rep-pipeline-filter mt-1">
                    {{ pipelineFilterLabel }}
                  </p> -->
                </div>
                <p class="mb-0">
                  <strong class="me-1" style="color: #E41F07;">Total:</strong>
                  <strong>{{ todaysCallbacks?.total ?? 0 }}</strong>
                </p>
              </div>
            </div>
            <callback-widget-table
              :items="todaysCallbackItems"
              mode="time"
              empty-message="No callbacks for today"
            />
          </div>
        </div>
      </div>

      <!-- Overdue Callbacks -->
      <div class="col-md-12 d-flex">
        <div class="card flex-fill sales-rep-widget-card">
          <div class="section-loader-overlay" v-if="isLoading">
            <span class="loader"></span>
          </div>
          <div class="card-body p-0">
            <div class="statistic-header p-3 border-bottom mb-0">
              <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 w-100">
                <div>
                  <h4 class="mb-1">
                    <router-link to="/crm/meter-opportunities-list">Overdue Quoted, Priority and Decision Maker Callbacks</router-link>
                  </h4>
                  <!-- <p v-if="pipelineFilterLabel" class="mb-0 small sales-rep-pipeline-filter mt-1">
                    {{ pipelineFilterLabel }}
                  </p> -->
                </div>
                <p class="mb-0">
                  <strong class="me-1" style="color: #E41F07;">Overdue:</strong>
                  <strong>{{ overdueCallbacks?.total ?? 0 }}</strong>
                </p>
              </div>
            </div>
            <callback-widget-table
              :items="overdueCallbackItems"
              mode="date"
              empty-message="No overdue callbacks"
            />
          </div>
        </div>
      </div>

      <!-- Issue Contracts -->
      <div class="col-md-12 d-flex">
        <div class="card flex-fill sales-rep-widget-card">
          <div class="section-loader-overlay" v-if="isLoading">
            <span class="loader"></span>
          </div>
          <div class="card-body p-0">
            <div class="statistic-header p-3 border-bottom mb-0">
              <h4 class="mb-0">
                <router-link to="/crm/contracts-list">Issue Contracts</router-link>
              </h4>
            </div>
            <issue-contracts-table
              :items="issueContractItems"
              :sort-by="issueSortBy"
              :sort-dir="issueSortDir"
              @sort="onIssueSort"
            />
          </div>
        </div>
      </div>

      <!-- Cancelled Contracts -->
      <div class="col-md-12 d-flex">
        <div class="card flex-fill sales-rep-widget-card">
          <div class="section-loader-overlay" v-if="isLoading">
            <span class="loader"></span>
          </div>
          <div class="card-body p-0">
            <div class="statistic-header p-3 border-bottom mb-0">
              <h4 class="mb-0">
                <router-link to="/crm/contracts-list">Cancelled Contracts</router-link>
              </h4>
              <span class="text-muted fw-normal ms-1">(Last 10 contracts)</span>
            </div>
            <cancelled-contracts-table :items="cancelledContractItems" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import CallbackWidgetTable from './sales-rep-callback-widget-table.vue';
import IssueContractsTable from './sales-rep-issue-contracts-table.vue';
import CancelledContractsTable from './sales-rep-cancelled-contracts-table.vue';

export default {
  name: 'SalesRepDashboardSections',
  components: {
    CallbackWidgetTable,
    IssueContractsTable,
    CancelledContractsTable,
  },
  props: {
    sections: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    issueSortBy: { type: String, default: 'date_of_sale' },
    issueSortDir: { type: String, default: 'desc' },
  },
  emits: ['issue-sort'],
  setup(props, { emit }) {
    const todaysCallbacks = computed(() => props.sections?.todays_callbacks ?? null);
    const overdueCallbacks = computed(() => props.sections?.overdue_callbacks ?? null);
    const issueContracts = computed(() => props.sections?.issue_contracts ?? null);
    const cancelledContracts = computed(() => props.sections?.cancelled_contracts ?? null);

    const todaysCallbackItems = computed(
      () => todaysCallbacks.value?.items?.data ?? []
    );
    const overdueCallbackItems = computed(
      () => overdueCallbacks.value?.items?.data ?? []
    );
    const issueContractItems = computed(() => issueContracts.value?.items ?? []);
    const cancelledContractItems = computed(() => cancelledContracts.value?.items ?? []);

    const pipelineFilterLabel = computed(() => {
      const filter =
        todaysCallbacks.value?.pipeline_filter ||
        overdueCallbacks.value?.pipeline_filter;
      if (!filter) return '';
      const types = (filter.pipeline_types || [])
        .map((t) => String(t).replace(/_/g, ' ').toUpperCase())
        .join(', ');
      const name = filter.pipeline_name || '';
      return [types, name].filter(Boolean).join(' — ');
    });

    const onIssueSort = (column) => {
      emit('issue-sort', column);
    };

    return {
      todaysCallbacks,
      overdueCallbacks,
      todaysCallbackItems,
      overdueCallbackItems,
      issueContractItems,
      cancelledContractItems,
      pipelineFilterLabel,
      onIssueSort,
    };
  },
};
</script>

<style scoped lang="scss">
.sales-rep-widget-card {
  position: relative;
  min-height: 200px;
}

.section-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.375rem;
}

.sales-rep-pipeline-filter {
  background-color: #fff3cd;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
}
</style>
